<template lang="html">
  <div class="payment">

    <h2 class="mb-4">Information</h2>
    <table class="information-table">
      <tbody class="body-1">
        <tr v-for="header in tableHeaders" :key="header.value">
          <td class="font-weight-bold pr-6 py-1 table-header">{{header.text}}</td>
          <td class="text--primary table-value" :class="'payment--' + header.value">{{header.morph ? header.morph(payment[header.value], payment, header) : payment[header.value]}}</td>
        </tr>
      </tbody>
    </table>

    <div class="d-flex">
      <v-spacer />
      <v-btn text @click="showExtra = !showExtra">{{showExtra ? 'Hide' : 'More'}}</v-btn>
    </div>

    <v-expand-transition>
      <div v-show="showExtra">
        <table class="information-table">
          <tbody class="body-1">
            <tr v-for="header in extraTableHeaders" :key="header.value">
              <td class="font-weight-bold pr-6 py-1 table-header">{{header.text}}</td>
              <td class="text--primary table-value" :class="'payment--' + header.value">{{header.morph ? header.morph(payment[header.value], payment, header) : payment[header.value]}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-expand-transition>

    <v-divider class="my-5" />

    <template v-if="payment.originalPayment || (payment.refundPayments && payment.refundPayments.length > 0) || payment.reversalPayment">
      <h2 class="mb-1">Connected payments</h2>

      <v-list>
        <v-list-item
          two-line
          v-if="payment.originalPayment"
          link
          @click="$emit('open-payment', payment.originalPayment)"
        >
          <v-list-item-content>
            <v-list-item-title>
              Original payment
            </v-list-item-title>
            <v-list-item-subtitle>
              Click to open
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          two-line
          v-if="payment.reversalPayment"
          link
          @click="$emit('open-payment', payment.reversalPayment)"
        >
          <v-list-item-content>
            <v-list-item-title>
              Reversal payment
            </v-list-item-title>
            <v-list-item-subtitle>
              Click to open
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item
          two-line
          v-for="refundPayment in (payment.refundPayments || [])" :key="refundPayment"
          link
          @click="$emit('open-payment', refundPayment)"
        >
          <v-list-item-content>
            <v-list-item-title>
              Refund
            </v-list-item-title>
            <v-list-item-subtitle>
              Click to open
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider class="my-5" />
    </template>

    <h2 class="mb-4">Items</h2>

    <v-list>
      <v-list-item two-line v-for="(cartItem, index) in payment.items" :key="cartItem.cartitemId || cartItem.id + '_' + index">
        <v-list-item-content>
          <v-list-item-title>
            <div class="d-flex">
              <span class="text-truncate d-block">{{cartItem.title}}</span>
              <v-spacer/>
              <span class="text-no-wrap">{{cartItem.price}} SEK</span>
            </div>
          </v-list-item-title>
          <v-list-item-subtitle>
            {{cartItem.itemgroupName}} (Vat {{cartItem.vat}}%)
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <v-divider class="my-5" />

    <h2 class="mb-4">History</h2>

    <v-list>
      <v-list-item three-line v-for="(historyItem, index) in history" :key="index">
        <v-list-item-content>
          <v-list-item-title>
            {{capitalizeFirstLetter(historyItem.action)}}
          </v-list-item-title>
          <v-list-item-subtitle>
            <v-tooltip bottom v-if="historyItem.relativeDateString">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >{{capitalizeFirstLetter(historyItem.relativeDateString + ' later')}}</span>
              </template>
              <span>{{historyItem.dateString}}</span>
            </v-tooltip>
            <span v-else>{{historyItem.dateString}}</span>
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            By {{historyItem.device}}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>


  </div>
</template>

<script>
import moment from 'moment-timezone'

var capitalizeFirstLetter = (string) => {
  string = string || '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export default {
  props: {
    item: {
      default: () => ({})
    }
  },
  watch: {
    item () {
      this.showExtra = false;
    }
  },
  data () {
    return {
      tableHeaders: [
        {
          text: 'Transaction Type',
          value: 'transactionType',
          morph: capitalizeFirstLetter
        },
        {
          text: 'Receipt Id',
          value: 'payeePaymentReference'
        },
        {
          text: 'Serial Number',
          value: 'serialNumber'
        },
        {
          text: 'Point Of Sale',
          value: 'pointOfSale'
        },
        {
          text: 'Operator',
          value: 'operatorName'
        },
        {
          text: 'Amount',
          value: 'amount',
          morph: (v) => (v == undefined ? '-' : v) +' SEK'
        },
        {
          text: 'VAT Total',
          value: 'items',
          morph: (items) => ((items || []).reduce((ac, i) => ac + this.getVAT(i.price, i.vat), 0)).toFixed(2) +' SEK'
        },
        {
          text: 'Status',
          value: 'status'
        },
        {
          text: 'Date created',
          value: 'dateCreated',
          morph: (v) => moment(v).format('YYYY DD MMM HH:mm:ss')
        },
        {
          text: 'Date paid',
          value: 'datePaid',
          morph: (v) => !v || v == 'None' ? '-' : moment(v).format('YYYY DD MMM HH:mm:ss')
        },
        {
          text: 'Payment method',
          value: 'paymentType'
        },
        {
          text: 'Payer alias',
          value: 'payerAlias'
        },
        {
          text: 'Installation Id',
          value: 'installationId'
        },
        {
          text: 'Z-Report nr.',
          value: 'paymentLockId'
        }
      ],
      extraTableHeaders: [
        {
          text: 'Control unit',
          value: 'controlUnitId'
        },
        {
          text: 'Control code',
          value: 'controlCode'
        },
        {
          text: 'Copy control unit',
          value: 'copyControlUnitId'
        },
        {
          text: 'Copy control code',
          value: 'copyControlCode'
        },
        {
          text: 'FTP version',
          value: 'ftp_version'
        },
        {
          text: 'Delivery time',
          value: 'deliveryTime'
        },
        {
          text: 'Delivery time status',
          value: 'deliveryTimeStatus'
        },
        {
          text: 'Is reversal',
          value: 'reversal',
          morph: (v) => v ? 'Yes' : 'No'
        }
      ],
      showExtra: false
    };
  },
  computed: {
    payment () {
      return this.item || {};
    },
    history () {
      var history = this.payment.history || [];
      var previousDate = null;
      var result = history.map((historyItem) => {
        var _result = {
          date: historyItem[0],
          action: historyItem[1],
          device: historyItem[2],
          itemgroups: historyItem[3]
        }
        var date = moment(_result.date);
        _result.dateString = date.format('YYYY DD MMM HH:mm:ss');
        if (previousDate) {
          _result.relativeDateString = date.from(previousDate, true);
        }
        previousDate = date;
        return _result;
      });
      return result;
    }
  },
  methods: {
    capitalizeFirstLetter,
    getNet (amount, vat) {
      // Get amount excl. VAT
      return amount / (1 + vat/100.0);
    },
    getVAT (amount, vat) {
      // Get VAT amount
      return this.getNet(amount, vat) * (vat/100.0);
    }
  }
}
</script>

<style lang="css" scoped>
.information-table {
  max-width: 100%;
}

.information-table .table-value {
  word-break: break-all;
  white-space: normal;
}
</style>
